import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, Loader2, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SpellcheckInput from "@/components/SpellcheckInput";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DespachoInfoCallout, DespachoSimpleTable } from "@/components/despacho/DespachoUi";
import { despachoFiscalInfoApi, parseFiscalInfoError } from "@/modules/despacho/despachoFiscalInfoApi";
import {
  buildPersistedRuleLabel,
  CALCULATION_TYPE_OPTIONS,
  DUE_RULE_AUTHORITY_FILTERS,
  FISCAL_AUTHORITY_FILTERS,
  formatRuleScheduleSummary,
  OPERATIONAL_AUTHORITY_FILTERS,
  RECURRENCE_PERIODICITY_OPTIONS,
  calculationTypeForPeriodicity,
  dueRuleScopeLabel,
  dueRuleStatusLabel,
  normalizeRulePeriodicity,
  periodicityDisplayLabel,
  recurrenceOptionsForPeriodicity,
  RULE_DEACTIVATION_REASONS,
} from "@/modules/despacho/dueDateRulesHelpers";
import { obligationAuthorityChipClass, obligationAuthorityLabel } from "@/modules/despacho/despachoFiscalInfoHelpers";
import {
  DESPACHO_READ_ONLY_BADGE,
  isDespachoReadOnly,
} from "@/modules/despacho/despachoPermissions";
import { useAuth } from "@/lib/auth";
import { useCapabilities } from "@/lib/useCapabilities";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";

const emptyEdit = {
  calculation_type: "fixed_day_next_month",
  day_of_month: 17,
  month: 3,
  months_after_period: 1,
  periodicity: "monthly",
  operational_offset_days: -7,
  reminder_offset_days: -5,
  calendar_enabled: true,
  active: true,
  rule_label: "",
  edit_motive: "",
  edit_comment: "",
};

function formatSampleDate(value) {
  const raw = String(value || "").slice(0, 10);
  if (!raw || raw.length < 10) return "—";
  const [y, m, d] = raw.split("-");
  return `${d}/${m}/${y}`;
}

function parseOffsetDays(value, fallback = 0) {
  const raw = String(value ?? "").trim();
  if (!raw) return fallback;
  const num = Number(raw);
  return Number.isFinite(num) ? num : null;
}

function parseDayOfMonth(value, fallback = 17) {
  const num = Number(value);
  if (!Number.isFinite(num) || num < 1 || num > 31) return fallback;
  return num;
}

function formatSaveSuccessMessage(saved, periodicity) {
  const schedule = formatRuleScheduleSummary(saved);
  const parts = [];
  if (schedule && schedule !== "—") parts.push(schedule);
  if (periodicity === "annual" && saved?.month) parts.push(`mes ${saved.month}`);
  if (saved?.calendar_enabled) parts.push("calendario activo");
  return parts.length ? `Regla actualizada (${parts.join(" · ")}).` : "Regla actualizada.";
}

export default function CalendarioDueRulesPanel({
  scope,
  authorityFilters: authorityFiltersProp,
  noticeText,
  testId = "calendario-due-rules-panel",
  showSampleDates = false,
  sampleDateColumns = ["fiscal", "operativa", "recordatorio"],
  hideAuthorityFilters = false,
  allowCustomAdd = false,
}) {
  const { user } = useAuth();
  const { permissions, role, isPlatformDesigner, loading: capabilitiesLoading } = useCapabilities();
  const readOnly = isDespachoReadOnly({ permissions, role, isPlatformDesigner, capabilitiesLoading, user });

  const [items, setItems] = useState([]);
  const [globalFallback, setGlobalFallback] = useState({ monthly_default_day: 17 });
  const [authorityFilter, setAuthorityFilter] = useState(
    () => (scope === "operational" ? "Interna" : "all"),
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [loadState, setLoadState] = useState("loading");
  const [loadMessage, setLoadMessage] = useState("");
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState(emptyEdit);
  const [deactivateMode, setDeactivateMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customForm, setCustomForm] = useState({
    nombre_visible: "",
    authority: scope === "operational" ? "Interna" : "SAT",
    periodicity: "monthly",
    day_of_month: 17,
    month: 3,
    operational_offset_days: -7,
    reminder_offset_days: -5,
    rule_label: "",
  });

  const authorityFilters = authorityFiltersProp != null
    ? authorityFiltersProp
    : (scope === "fiscal" ? FISCAL_AUTHORITY_FILTERS : scope === "operational" ? OPERATIONAL_AUTHORITY_FILTERS : DUE_RULE_AUTHORITY_FILTERS);

  const samplePeriodLabel = useMemo(() => {
    const row = items.find((r) => r.sample_period);
    return row?.sample_period || "";
  }, [items]);

  const mergeSavedRuleRow = useCallback((saved) => {
    const oid = saved?.obligation_catalog_id;
    if (!oid) return;
    setItems((prev) => prev.map((row) => (
      row.obligation_catalog_id === oid ? { ...row, ...saved } : row
    )));
  }, []);

  const loadRules = useCallback(async () => {
    setLoadState("loading");
    setLoadMessage("");
    try {
      const data = await despachoFiscalInfoApi.listDueDateRules({
        authority: authorityFilter !== "all" ? authorityFilter : undefined,
        q: searchQuery || undefined,
        scope: scope || undefined,
      });
      setItems(data.items || []);
      setGlobalFallback(data.globalFallback || { monthly_default_day: 17 });
      setLoadState("ready");
    } catch (err) {
      const parsed = parseFiscalInfoError(err);
      setItems([]);
      setLoadState(parsed.kind === "forbidden" ? "forbidden" : "error");
      setLoadMessage(parsed.message);
    }
  }, [authorityFilter, searchQuery, scope]);

  useEffect(() => {
    const t = setTimeout(() => loadRules(), searchQuery ? 250 : 0);
    return () => clearTimeout(t);
  }, [loadRules, searchQuery]);

  const openEdit = (row) => {
    setEditRow(row);
    setDeactivateMode(false);
    const periodicity = normalizeRulePeriodicity(row.periodicity);
    setEditForm({
      ...emptyEdit,
      calculation_type: row.calculation_type || "editable_manual",
      day_of_month: row.day_of_month ?? 17,
      month: row.month ?? 3,
      months_after_period: row.months_after_period ?? 1,
      periodicity,
      operational_offset_days: row.operational_offset_days ?? -7,
      reminder_offset_days: row.reminder_offset_days ?? -5,
      calendar_enabled: row.has_workspace_override
        ? row.calendar_enabled !== false
        : (scope === "operational" ? true : row.calendar_enabled !== false),
      active: row.active !== false,
      rule_label: row.rule_label || "",
      edit_motive: "",
      edit_comment: "",
    });
  };

  const openDeactivate = (row) => {
    openEdit(row);
    setDeactivateMode(true);
  };

  const loadHistory = async (row) => {
    try {
      const rows = await despachoFiscalInfoApi.getDueDateRuleHistory(row.obligation_catalog_id);
      setHistory(rows);
      setHistoryOpen(true);
    } catch {
      toast.error("No se pudo cargar el historial.");
    }
  };

  const saveEdit = async (event) => {
    event?.preventDefault?.();
    if (!editRow?.obligation_catalog_id) {
      toast.error("No se identificó la obligación a editar.");
      return;
    }
    if (deactivateMode && !editForm.edit_motive) {
      toast.error("Seleccione un motivo de desactivación.");
      return;
    }
    if (!deactivateMode && !String(editForm.edit_motive || "").trim()) {
      toast.error("Indique el motivo del cambio.");
      return;
    }
    const operationalOffset = parseOffsetDays(editForm.operational_offset_days, -7);
    const reminderOffset = parseOffsetDays(editForm.reminder_offset_days, -5);
    if (operationalOffset === null || reminderOffset === null) {
      toast.error("Los offsets operativo y de recordatorio deben ser números válidos (-60 a 60).");
      return;
    }
    setBusy(true);
    try {
      const periodicity = normalizeRulePeriodicity(editForm.periodicity);
      const dayOfMonth = parseDayOfMonth(editForm.day_of_month);
      const calendarEnabled = deactivateMode
        ? editForm.calendar_enabled
        : (scope === "operational" ? editForm.calendar_enabled !== false : Boolean(editForm.calendar_enabled));
      const payload = {
        calculation_type: editForm.calculation_type,
        day_of_month: dayOfMonth,
        months_after_period: Number(editForm.months_after_period) || 1,
        periodicity,
        operational_offset_days: operationalOffset,
        reminder_offset_days: reminderOffset,
        calendar_enabled: calendarEnabled,
        active: deactivateMode ? false : editForm.active,
        rule_label: buildPersistedRuleLabel({
          rule_label: editForm.rule_label,
          day_of_month: dayOfMonth,
          operational_offset_days: operationalOffset,
          reminder_offset_days: reminderOffset,
        }),
        edit_motive: String(editForm.edit_motive || "").trim(),
        edit_comment: String(editForm.edit_comment || "").trim(),
      };
      if (periodicity === "annual") {
        payload.month = Number(editForm.month) || null;
      }
      let saved;
      if (deactivateMode) {
        saved = await despachoFiscalInfoApi.saveDueDateRule(editRow.obligation_catalog_id, {
          ...payload,
          deactivation_reason: editForm.edit_motive,
          deactivation_notes: editForm.edit_comment,
        });
        toast.success("Regla desactivada.");
      } else {
        saved = await despachoFiscalInfoApi.saveDueDateRule(editRow.obligation_catalog_id, payload);
        toast.success(formatSaveSuccessMessage(saved, periodicity));
      }
      if (saved) mergeSavedRuleRow(saved);
      setEditRow(null);
      await loadRules();
    } catch (err) {
      const parsed = parseFiscalInfoError(err);
      const isTimeout = err?.code === "ECONNABORTED" || /timeout/i.test(String(err?.message || ""));
      toast.error(isTimeout
        ? "La operación tardó demasiado. Compruebe que el backend esté actualizado y reiniciado."
        : parsed.message, { duration: parsed.kind === "license_blocked" ? 12000 : 6000 });
    } finally {
      setBusy(false);
    }
  };

  const reactivate = async (row) => {
    setBusy(true);
    try {
      await despachoFiscalInfoApi.saveDueDateRule(row.obligation_catalog_id, {
        active: true,
        calendar_enabled: true,
        edit_motive: "reactivacion",
      });
      toast.success("Regla reactivada.");
      await loadRules();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const saveCustom = async () => {
    if (!customForm.nombre_visible.trim()) {
      toast.error("Indique el nombre de la obligación o tarea.");
      return;
    }
    setBusy(true);
    try {
      await despachoFiscalInfoApi.createCustomCalendarObligation({
        nombre_visible: customForm.nombre_visible.trim(),
        authority: customForm.authority,
        periodicity: normalizeRulePeriodicity(customForm.periodicity),
        scope: scope === "operational" ? "operational" : "fiscal",
        calculation_type: calculationTypeForPeriodicity(customForm.periodicity),
        day_of_month: Number(customForm.day_of_month) || 17,
        month: normalizeRulePeriodicity(customForm.periodicity) === "annual" ? Number(customForm.month) || 3 : undefined,
        operational_offset_days: Number(customForm.operational_offset_days),
        reminder_offset_days: Number(customForm.reminder_offset_days),
        rule_label: customForm.rule_label || customForm.nombre_visible.trim(),
      });
      toast.success("Regla agregada.");
      setCustomOpen(false);
      setCustomForm({
        nombre_visible: "",
        authority: scope === "operational" ? "Interna" : "SAT",
        periodicity: "monthly",
        day_of_month: 17,
        month: 3,
        operational_offset_days: -7,
        reminder_offset_days: -5,
        rule_label: "",
      });
      await loadRules();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const deleteCustom = async (row) => {
    if (readOnly || !row.is_workspace_custom) return;
    if (!window.confirm(`¿Eliminar «${row.nombre_visible}»?`)) return;
    setBusy(true);
    try {
      await despachoFiscalInfoApi.deleteCustomCalendarObligation(row.obligation_catalog_id);
      toast.success("Eliminada.");
      await loadRules();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const tableColumns = useMemo(() => {
    const cols = [
      { key: "nombre_visible", label: "Obligación" },
      {
        key: "authority",
        label: "Autoridad",
        render: (r) => (
          <span className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-sm border ${obligationAuthorityChipClass(r.authority)}`}>
            {obligationAuthorityLabel(r.authority)}
          </span>
        ),
      },
      {
        key: "periodicity_label",
        label: "Periodicidad",
        render: (r) => periodicityDisplayLabel(r.periodicity) || r.periodicity_label || "—",
      },
      {
        key: "rule_label",
        label: "Regla",
        render: (r) => (
          <span className="text-xs text-slate-700">
            {formatRuleScheduleSummary(r)}
            {r.is_fallback ? (
              <Badge variant="secondary" className="ml-1 rounded-sm text-[9px]">Fallback</Badge>
            ) : null}
          </span>
        ),
      },
    ];
    if (showSampleDates && sampleDateColumns.includes("fiscal")) {
      cols.push({
        key: "sample_fecha_fiscal",
        label: samplePeriodLabel ? `Vence (${samplePeriodLabel})` : "Vence ejemplo",
        render: (r) => formatSampleDate(r.sample_fecha_fiscal),
      });
    }
    if (showSampleDates && sampleDateColumns.includes("operativa")) {
      cols.push({
        key: "sample_fecha_operativa",
        label: "Plazo operativo",
        render: (r) => formatSampleDate(r.sample_fecha_operativa),
      });
    }
    if (showSampleDates && sampleDateColumns.includes("recordatorio")) {
      cols.push({
        key: "sample_fecha_recordatorio",
        label: "Recordatorio cliente",
        render: (r) => formatSampleDate(r.sample_fecha_recordatorio),
      });
    }
    cols.push(
      {
        key: "status",
        label: "Estado",
        render: (r) => (
          <span className="text-xs">{dueRuleStatusLabel(r)}</span>
        ),
      },
      {
        key: "actions",
        label: "Acción",
        render: (r) => (
          <div className="flex flex-wrap gap-1">
            {!readOnly && r.active !== false ? (
              <>
                <Button type="button" size="sm" variant="outline" className="rounded-sm h-7 text-[11px]" onClick={() => openEdit(r)}>
                  Editar
                </Button>
                <Button type="button" size="sm" variant="outline" className="rounded-sm h-7 text-[11px]" onClick={() => openDeactivate(r)}>
                  Desactivar
                </Button>
              </>
            ) : null}
            {!readOnly && r.active === false ? (
              <Button type="button" size="sm" variant="outline" className="rounded-sm h-7 text-[11px]" onClick={() => reactivate(r)}>
                Reactivar
              </Button>
            ) : null}
            <Button type="button" size="sm" variant="ghost" className="rounded-sm h-7 text-[11px]" onClick={() => loadHistory(r)}>
              Historial
            </Button>
            {!readOnly && r.is_workspace_custom ? (
              <Button type="button" size="sm" variant="ghost" className="rounded-sm h-7 text-[11px] text-rose-700" onClick={() => deleteCustom(r)}>
                <Trash2 className="h-3 w-3 mr-1" aria-hidden />
                Eliminar
              </Button>
            ) : null}
          </div>
        ),
      },
    );
    return cols;
  }, [showSampleDates, sampleDateColumns, samplePeriodLabel, readOnly]);

  const tableRows = useMemo(() => items, [items]);

  return (
    <div className="space-y-4" data-testid={testId}>
      <DespachoInfoCallout testId={`${testId}-notice`}>
        {noticeText || (
          <>
            Define cómo vence cada obligación del catálogo maestro. Si no hay regla específica, se usa el fallback
            global (día <strong>{globalFallback.monthly_default_day ?? 17}</strong> del mes siguiente).
            Los cambios afectan futuras generaciones; las instancias ya creadas no se recalculan aquí.
          </>
        )}
      </DespachoInfoCallout>

      {readOnly ? (
        <p className="text-xs text-slate-500 m-0">{DESPACHO_READ_ONLY_BADGE} — solo lectura.</p>
      ) : null}

      {!readOnly && allowCustomAdd ? (
        <Button type="button" size="sm" className="rounded-sm bg-[#0F2D5C] hover:bg-[#0a2248]" onClick={() => setCustomOpen(true)}>
          <Plus className="h-3.5 w-3.5 mr-1" aria-hidden />
          Agregar regla del despacho
        </Button>
      ) : null}

      <div className="relative max-w-md">
        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" aria-hidden />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Buscar obligación…"
          className="pl-8 rounded-sm h-9 text-sm"
        />
      </div>

      {!hideAuthorityFilters && authorityFilters.length > 1 ? (
        <div className="flex flex-wrap gap-1.5">
          {authorityFilters.map((f) => (
            <Button
              key={f.key}
              type="button"
              size="sm"
              variant={authorityFilter === f.key ? "default" : "outline"}
              className={`rounded-sm h-7 text-[11px] ${authorityFilter === f.key ? "bg-[#0F2D5C] hover:bg-[#0a2248]" : ""}`}
              onClick={() => setAuthorityFilter(f.key)}
            >
              {f.label}
            </Button>
          ))}
        </div>
      ) : null}

      {loadState === "loading" ? (
        <div className="flex items-center gap-2 text-sm text-slate-600 py-4">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Cargando reglas…
        </div>
      ) : null}

      {loadState === "error" || loadState === "forbidden" ? (
        <div className="rounded-sm border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-950 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
          <p className="m-0">{loadMessage}</p>
        </div>
      ) : null}

      {loadState === "ready" ? (
        <DespachoSimpleTable
          columns={tableColumns}
          rows={tableRows}
          emptyLabel="Sin reglas para el filtro actual."
        />
      ) : null}

      <Dialog open={Boolean(editRow)} onOpenChange={(open) => !open && !busy && setEditRow(null)}>
        <DialogContent
          className="rounded-sm sm:max-w-lg max-h-[90vh] overflow-y-auto"
          onInteractOutside={(event) => busy && event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{deactivateMode ? "Desactivar regla" : "Editar regla de vencimiento"}</DialogTitle>
            <DialogDescription>
              {editRow?.nombre_visible}
              {!deactivateMode ? (
                <> · Alcance: {dueRuleScopeLabel(editRow)}. Este cambio afectará futuras generaciones de vencimientos.</>
              ) : (
                <> · La regla no participará en futuras generaciones.</>
              )}
            </DialogDescription>
          </DialogHeader>
          {!deactivateMode ? (
            <form className="space-y-3 py-2" onSubmit={saveEdit}>
              <div className="space-y-1.5">
                <Label className="text-xs">Forma repetitiva</Label>
                <Select
                  value={normalizeRulePeriodicity(editForm.periodicity)}
                  onValueChange={(v) => setEditForm((f) => ({
                    ...f,
                    periodicity: v,
                    calculation_type: calculationTypeForPeriodicity(v),
                  }))}
                >
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {recurrenceOptionsForPeriodicity(editForm.periodicity).map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Tipo de cálculo</Label>
                <Select value={editForm.calculation_type} onValueChange={(v) => setEditForm((f) => ({ ...f, calculation_type: v }))}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CALCULATION_TYPE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className={`grid gap-3 ${normalizeRulePeriodicity(editForm.periodicity) === "annual" ? "grid-cols-2" : "grid-cols-1"}`}>
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    {normalizeRulePeriodicity(editForm.periodicity) === "annual" ? "Día del mes (anual)" : "Día del mes"}
                  </Label>
                  <Input type="number" min={1} max={31} className="rounded-sm h-9" value={editForm.day_of_month ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, day_of_month: e.target.value }))} />
                </div>
                {normalizeRulePeriodicity(editForm.periodicity) === "annual" ? (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Mes (1–12)</Label>
                    <Input type="number" min={1} max={12} className="rounded-sm h-9" value={editForm.month ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, month: e.target.value }))} />
                  </div>
                ) : null}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Offset operativo (días)</Label>
                  <Input type="number" className="rounded-sm h-9" value={editForm.operational_offset_days} onChange={(e) => setEditForm((f) => ({ ...f, operational_offset_days: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Offset recordatorio (días)</Label>
                  <Input type="number" className="rounded-sm h-9" value={editForm.reminder_offset_days} onChange={(e) => setEditForm((f) => ({ ...f, reminder_offset_days: e.target.value }))} />
                </div>
              </div>
              <div className="flex items-center justify-between gap-3 rounded-sm border border-slate-200 px-3 py-2">
                <div>
                  <Label className="text-xs">Usar en calendario</Label>
                  <p className="text-[11px] text-slate-500 m-0">Calcula fechas de ejemplo y participa en generación de vencimientos.</p>
                </div>
                <Switch
                  checked={editForm.calendar_enabled}
                  onCheckedChange={(checked) => setEditForm((f) => ({ ...f, calendar_enabled: checked }))}
                  aria-label="Usar en calendario"
                />
              </div>
              <SpellcheckInput
                label="Etiqueta de regla (opcional)"
                className="rounded-sm h-9"
                value={editForm.rule_label}
                onChange={(e) => setEditForm((f) => ({ ...f, rule_label: e.target.value }))}
                placeholder="Texto visible en la tabla"
                testId="due-rule-edit-label"
              />
              <SpellcheckInput
                label="Motivo del cambio"
                className="rounded-sm h-9"
                value={editForm.edit_motive}
                onChange={(e) => setEditForm((f) => ({ ...f, edit_motive: e.target.value }))}
                testId="due-rule-edit-motive"
              />
              <SpellcheckTextarea
                label="Comentario"
                className="rounded-sm min-h-[72px]"
                value={editForm.edit_comment}
                onChange={(e) => setEditForm((f) => ({ ...f, edit_comment: e.target.value }))}
                testId="due-rule-edit-comment"
              />
              <DialogFooter className="pt-2 sm:justify-end">
                <Button type="button" variant="outline" className="rounded-sm" onClick={() => setEditRow(null)} disabled={busy}>
                  Cancelar
                </Button>
                <Button type="submit" className="rounded-sm bg-[#0F2D5C] hover:bg-[#0a2248]" disabled={busy}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-3 py-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Motivo</Label>
                <Select value={editForm.edit_motive} onValueChange={(v) => setEditForm((f) => ({ ...f, edit_motive: v }))}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Seleccione motivo…" /></SelectTrigger>
                  <SelectContent>
                    {RULE_DEACTIVATION_REASONS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <SpellcheckTextarea
                label="Comentario"
                className="rounded-sm min-h-[72px]"
                value={editForm.edit_comment}
                onChange={(e) => setEditForm((f) => ({ ...f, edit_comment: e.target.value }))}
                testId="due-rule-deactivate-comment"
              />
              <DialogFooter className="pt-2 sm:justify-end">
                <Button type="button" variant="outline" className="rounded-sm" onClick={() => setEditRow(null)} disabled={busy}>
                  Cancelar
                </Button>
                <Button type="button" className="rounded-sm bg-[#0F2D5C] hover:bg-[#0a2248]" disabled={busy} onClick={saveEdit}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Desactivar"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="rounded-sm sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Historial de cambios</DialogTitle>
            <DialogDescription>Últimos movimientos en esta regla (espacio de trabajo).</DialogDescription>
          </DialogHeader>
          <ul className="text-xs text-slate-700 space-y-2 max-h-64 overflow-y-auto m-0 pl-4">
            {history.length ? history.map((h) => (
              <li key={h.id}>
                <strong>{String(h.created_at || "").slice(0, 16)}</strong> — {h.action}
                {h.motive ? ` · ${h.motive}` : ""}
              </li>
            )) : (
              <li>Sin historial en este espacio de trabajo.</li>
            )}
          </ul>
        </DialogContent>
      </Dialog>

      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent className="rounded-sm sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Agregar {scope === "operational" ? "plazo operativo" : "obligación fiscal"} personalizada</DialogTitle>
            <DialogDescription>Quedará disponible solo en este despacho para generar vencimientos desde Tareas.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Nombre</Label>
              <Input className="rounded-sm h-9" value={customForm.nombre_visible} onChange={(e) => setCustomForm((f) => ({ ...f, nombre_visible: e.target.value }))} />
            </div>
            {scope !== "operational" ? (
              <div className="space-y-1.5">
                <Label className="text-xs">Autoridad</Label>
                <Select value={customForm.authority} onValueChange={(v) => setCustomForm((f) => ({ ...f, authority: v }))}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FISCAL_AUTHORITY_FILTERS.filter((f) => f.key !== "all").map((f) => (
                      <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div className="space-y-1.5">
              <Label className="text-xs">Forma repetitiva</Label>
              <Select
                value={normalizeRulePeriodicity(customForm.periodicity)}
                onValueChange={(v) => setCustomForm((f) => ({ ...f, periodicity: v }))}
              >
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RECURRENCE_PERIODICITY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className={`grid gap-3 ${normalizeRulePeriodicity(customForm.periodicity) === "annual" ? "grid-cols-2" : "grid-cols-1"}`}>
              <div className="space-y-1.5">
                <Label className="text-xs">Día del mes</Label>
                <Input type="number" min={1} max={31} className="rounded-sm h-9" value={customForm.day_of_month} onChange={(e) => setCustomForm((f) => ({ ...f, day_of_month: e.target.value }))} />
              </div>
              {normalizeRulePeriodicity(customForm.periodicity) === "annual" ? (
                <div className="space-y-1.5">
                  <Label className="text-xs">Mes (1–12)</Label>
                  <Input type="number" min={1} max={12} className="rounded-sm h-9" value={customForm.month ?? 3} onChange={(e) => setCustomForm((f) => ({ ...f, month: e.target.value }))} />
                </div>
              ) : null}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => setCustomOpen(false)}>Cancelar</Button>
            <Button type="button" className="rounded-sm bg-[#0F2D5C] hover:bg-[#0a2248]" disabled={busy} onClick={saveCustom}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Agregar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
